import React, {useState} from 'react';
import {Box} from '@mui/material';
import {fetchSlugForUniversity} from 'src/redux/admin/actions/universities';
import {EducationPhase} from 'src/types/enums';
import UniversityDropdown from 'src/views/admin/UniversityDropdown';
import ScholarshipProgramDropdown from 'src/components/ScholarshipProgramDropdown';
import FormikDropdown from 'src/components/formik-dropdown';
import Label from 'shared/components/label';
import FileUploadForm from 'src/components/FileUploadForm';
import {Field} from 'formik';

const UGSS = 'ugss';
const ENROLLMENT = 'enrollment';
const ITEMS = 'items';

const FILE_TYPE_OPTIONS = [
  {_id: UGSS, value: UGSS, text: 'Student Success'},
  {_id: ENROLLMENT, value: ENROLLMENT, text: 'Enrollment (Super Targeted Program)'},
  {_id: ITEMS, value: ITEMS, text: 'Items (UGSS items)'},
];

const EDUCATION_PHASES_FOR_FILE_TYPE = {
  [UGSS]: [EducationPhase.UNDERGRADUATE],
  [ITEMS]: [EducationPhase.UNDERGRADUATE],
  [ENROLLMENT]: [EducationPhase.HIGH_SCHOOL, EducationPhase.COMMUNITY_COLLEGE],
};

interface OptionsProps {
  slug?: string;
  fileType?: string;
  scholarshipProgramId?: string;
}

const UserDataFileUploader = (): React.ReactElement => {
  const [universityId, setUniversityId] = useState<string | null>(null);

  const validateOptions = (options: OptionsProps): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (options.fileType === ENROLLMENT && !options.scholarshipProgramId) {
      errors.scholarshipProgramId = 'Must select a scholarship program id';
    }

    if (!options.slug) {
      errors.slug = 'University must be selected';
    }

    return errors;
  };

  const modifyOptions = (options: OptionsProps): Record<string, string> => {
    const pathParts = [options.slug];

    if (options.fileType === ITEMS) {
      pathParts.push('items');
    } else {
      pathParts.push('users');
      pathParts.push(options.fileType);
    }

    const modifiedOptions: Record<string, string> = {
      path: pathParts.join('/'),
      add_row_number: 'true',
    };

    if (options.scholarshipProgramId) {
      modifiedOptions.columns_to_add = 'scholarship_program_id';
      modifiedOptions.scholarship_program_id = options.scholarshipProgramId;
    }

    return modifiedOptions;
  };

  return (
    <FileUploadForm
      title="Upload user data file"
      fileDescription="Selected a CSV file"
      uploadType="csv"
      initialOptionsValues={{fileType: UGSS}}
      modifyOptions={modifyOptions}
      validateOptions={validateOptions}
      resetAfterSubmit={false}
    >
      <Box py={1}>
        Select a file to upload, and the scholarship program to associate the students with
        (required for enrollment files, optional for ugss).
      </Box>
      <Box py={2}>
        <Label>
          File Type
          <FormikDropdown name="fileType" options={FILE_TYPE_OPTIONS} />
        </Label>
      </Box>
      <Box py={2}>
        <Field name="slug">
          {({form: {setFieldValue, values}}): React.ReactElement => (
            <UniversityDropdown
              autoSelectFirstUniversity
              value={universityId}
              onChange={(id): void => {
                setUniversityId(id);
                setFieldValue('scholarshipProgramId', null);
                fetchSlugForUniversity(id).then((slug) => setFieldValue('slug', slug));
              }}
              educationPhases={EDUCATION_PHASES_FOR_FILE_TYPE[values.fileType]}
            />
          )}
        </Field>
      </Box>
      <Box py={2}>
        <Field name="scholarshipProgramId">
          {({field: {value}, form: {setFieldValue, values}}): React.ReactElement | null =>
            values.fileType === ITEMS ? null : (
              <ScholarshipProgramDropdown
                disabled={!universityId}
                educationPhase={values.fileType === UGSS ? EducationPhase.UNDERGRADUATE : null}
                sponsorId={universityId}
                value={value}
                onChange={(scholarshipProgramId): void =>
                  setFieldValue('scholarshipProgramId', scholarshipProgramId)
                }
              />
            )
          }
        </Field>
      </Box>
    </FileUploadForm>
  );
};

export default UserDataFileUploader;
